/**
 * Advertising Service, web port of the iOS `advertisingService.ts`.
 *
 * Fetches paid-advertiser banners from Supabase `adverts` table (with
 * state-based targeting via `advert_locations` ↔ `business_locations`),
 * rotates them per screen with no-repeat-until-exhausted logic, and
 * records impressions + clicks via the existing
 * `increment_ad_impressions` / `increment_ad_clicks` RPCs and
 * `ad_clicks` table that the iOS app already uses.
 *
 * Surface: free-tier users only; premium users see no ads (handled in
 * the `AdBanner` component that calls this service).
 */
import { getSupabase } from '../lib/supabaseClient';

// Australian state code ↔ full-name aliases, the database stores both
// formats (e.g. "SA" and "South Australia") so we normalise on read.
const STATE_MAPPINGS = {
  SA:  ['SA',  'South Australia',              'south australia'],
  NSW: ['NSW', 'New South Wales',              'new south wales'],
  VIC: ['VIC', 'Victoria',                     'victoria'],
  QLD: ['QLD', 'Queensland',                   'queensland'],
  WA:  ['WA',  'Western Australia',            'western australia'],
  TAS: ['TAS', 'Tasmania',                     'tasmania'],
  ACT: ['ACT', 'Australian Capital Territory', 'australian capital territory'],
  NT:  ['NT',  'Northern Territory',           'northern territory'],
};

const normalizeState = (state) => {
  if (!state) return null;
  const s = String(state).toLowerCase().trim();
  for (const [code, aliases] of Object.entries(STATE_MAPPINGS)) {
    if (aliases.some((a) => a.toLowerCase() === s)) return code;
  }
  return state;
};

const statesMatch = (a, b) => {
  if (!a || !b) return false;
  return normalizeState(a) === normalizeState(b);
};

// 5-minute cache of the adverts list to keep page navigation snappy.
let advertsCache = [];
let cacheAt = 0;
let cachedState = null;
const CACHE_MS = 5 * 60 * 1000;

// Per-screen no-repeat tracking, same shape as iOS so the rotation
// feels identical when the user paginates between tabs.
const shownByScreen = new Map();

// 2026-09-08: Session-wide impression cap. Prevents any single
// advertiser from being shown to the same visitor more than
// SESSION_IMPRESSION_CAP times across the whole visit — spreads
// reach across the full inventory instead of one brand dominating
// a browsing session. Backed by sessionStorage so it survives SPA
// route changes and same-tab reloads, but resets when the tab is
// closed (which is the definition of a "session").
const SESSION_IMPRESSION_CAP = 3;
const SESSION_STORAGE_KEY = 'bp:ad-session-impressions';

const readSessionImpressions = () => {
  try {
    const raw = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed && typeof parsed === 'object') ? parsed : {};
  } catch (_) {
    return {};
  }
};

const writeSessionImpressions = (state) => {
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(state));
  } catch (_) { /* quota / privacy mode - ignore */ }
};

// Exposed for testing + admin overrides. Not used in production paths.
export const clearSessionImpressions = () => writeSessionImpressions({});

// Returns the set of advert ids that have already hit the session cap.
const cappedAdvertIds = () => {
  const counts = readSessionImpressions();
  const capped = new Set();
  for (const [id, n] of Object.entries(counts)) {
    if (typeof n === 'number' && n >= SESSION_IMPRESSION_CAP) capped.add(id);
  }
  return capped;
};

export const clearShownAdsForScreen = (screenLocation) => {
  shownByScreen.delete(screenLocation);
};

export const clearAllShownAds = () => shownByScreen.clear();

export const clearAdvertsCache = () => {
  advertsCache = [];
  cacheAt = 0;
  cachedState = null;
};

/**
 * findSponsoredAnswer(query) — used by the homepage search dropdown
 * to surface a native "sponsored answer" tile above organic results.
 *
 * Design principles:
 *   • RELEVANCE: match query tokens against the advertiser's name,
 *     description, topics AND category tags. Zero-score advertisers
 *     never appear.
 *   • LOCATION: `getAdverts` already applies state gating via
 *     `getUserState()`, so the pool is pre-filtered to the viewer's
 *     state. No cross-state ads leak through.
 *   • PROBABILISTIC: only shows ~40% of the time even when a match
 *     exists, so users don't see an ad on every single keystroke.
 *     Stable per query (same query in same session → same decision)
 *     so it doesn't flicker as the user types the same word.
 *   • SESSION-CAPPED: piggybacks on the shared 3-per-brand session
 *     cap, so a single advertiser can't hijack every search.
 */
export const findSponsoredAnswer = async (query) => {
  const q = String(query || '').toLowerCase().trim();
  if (q.length < 3) return null;

  const ads = await getAdverts();
  if (!ads.length) return null;

  // Drop session-capped advertisers up front.
  const capped = cappedAdvertIds();
  const pool = ads.filter((a) => !capped.has(a.id));
  if (!pool.length) return null;

  // Tokenise query (drop stop-words + tokens < 3 chars).
  const STOP = new Set(['and','the','for','with','from','into','are','was','has','get','how','can','who','you','out','all','off','new','not','but','our','any','use','one','two']);
  const tokens = q.split(/\s+/).filter((t) => t.length >= 3 && !STOP.has(t));
  if (!tokens.length) return null;

  // Extract host tokens from the advertiser's redirect / website URL
  // so a search for e.g. "fairmont" matches an advertiser whose
  // redirect_url is fairmonthomes.com.au even if the exact word isn't
  // in name/topics/description. This is one of the biggest lifts for
  // "search-for-a-brand" matching.
  const domainTokens = (url) => {
    try {
      const u = new URL(String(url || '').startsWith('http') ? url : `https://${url}`);
      const host = u.hostname.replace(/^www\./, '');
      return host.split('.').flatMap((seg) => seg.split(/[-_]/)).filter((s) => s.length >= 3);
    } catch (_) { return []; }
  };

  // Category-to-search-keyword expansion. Users search naturally
  // ("home loan", "kitchen", "double storey") but our advertiser
  // rows carry short category slugs. This map lifts common query
  // phrases into slug matches so ads surface more often when the
  // topic overlaps the advertiser's business type.
  const SYNONYMS = {
    'builder':        ['builder', 'construction', 'homes'],
    'custom':         ['custom', 'bespoke', 'architect'],
    'single':         ['single-storey', 'one-storey'],
    'double':         ['double-storey', 'two-storey'],
    'storey':         ['storey', 'story'],
    'knockdown':      ['kdr', 'knockdown', 'demolition'],
    'rebuild':        ['kdr', 'rebuild'],
    'first':          ['first-home', 'fhb'],
    'home':           ['home', 'homes', 'house'],
    'loan':           ['loan', 'mortgage', 'finance', 'broker'],
    'mortgage':       ['loan', 'mortgage', 'finance'],
    'finance':        ['loan', 'finance', 'broker'],
    'inspection':     ['inspection', 'inspector', 'defect'],
    'defect':         ['inspection', 'defect', 'quality'],
    'kitchen':        ['kitchen', 'appliance', 'joinery'],
    'appliance':      ['appliance', 'kitchen', 'whitegoods'],
    'bathroom':       ['bathroom', 'tiling', 'plumbing'],
    'electrical':     ['electrical', 'electrician', 'solar'],
    'solar':          ['solar', 'electrical', 'battery'],
    'granny':         ['granny-flat', 'granny'],
    'flat':           ['granny-flat', 'flat'],
    'land':           ['land', 'block', 'estate'],
    'block':          ['land', 'block'],
    'package':        ['package', 'h&l', 'house-and-land'],
    'landscape':      ['landscape', 'landscaping', 'garden', 'turf'],
    'landscaping':    ['landscape', 'landscaping', 'outdoor'],
    'fence':          ['fence', 'fencing'],
    'fencing':        ['fencing', 'fence'],
    'driveway':       ['driveway', 'concrete', 'paving'],
    'pool':           ['pool', 'swimming-pool'],
    'design':         ['design', 'floor-plan', 'architect'],
    'plan':           ['floor-plan', 'design'],
  };
  const expanded = new Set(tokens);
  for (const t of tokens) {
    for (const syn of (SYNONYMS[t] || [])) expanded.add(syn);
  }

  // Score each ad. Weightings tuned so a strong single hit (query
  // token matching the brand name or its domain) beats a weak
  // multi-hit (query token in a generic description).
  //   +8 exact token in advertiser.name  (brand-name search)
  //   +7 token in the redirect/website hostname
  //   +5 token in topics/categories (business type match)
  //   +3 token in description
  //   +2 synonym-expanded token in name/topics/hostname
  //   +2 whole query is a substring of name (multi-word brand)
  const scored = pool.map((ad) => {
    const name = String(ad.name || '').toLowerCase();
    const desc = String(ad.description || ad.tagline || '').toLowerCase();
    const topicHay = [
      ...(Array.isArray(ad.topics) ? ad.topics : []),
      ...(Array.isArray(ad.categories) ? ad.categories : []),
    ].join(' ').toLowerCase();
    const domainHay = [
      ...domainTokens(ad.redirect_url),
      ...domainTokens(ad.website_url),
    ].join(' ').toLowerCase();
    let score = 0;
    for (const t of tokens) {
      if (name.includes(t)) score += 8;
      if (domainHay.includes(t)) score += 7;
      if (topicHay.includes(t)) score += 5;
      if (desc.includes(t)) score += 3;
    }
    for (const t of expanded) {
      if (tokens.has && tokens.has(t)) continue; // already scored above
      if (!tokens.includes(t)) {
        if (name.includes(t)) score += 2;
        if (topicHay.includes(t)) score += 2;
        if (domainHay.includes(t)) score += 2;
      }
    }
    // Bonus: whole query appears in the brand name (helps two-word
    // brand searches like "loan market" or "house inspect").
    if (q.length >= 4 && name.includes(q)) score += 2;
    return { ad, score };
  }).filter((x) => x.score > 0);

  if (!scored.length) return null;

  // 70% probability gate — bumped from 55% so users see the ad
  // slightly more often. Stable per query so no flicker as they type.
  const hash = hashQuery(q);
  const gate = hash / 100; // 0.00 .. 0.99
  if (gate >= 0.70) return null;

  // Sort by score desc, then pick the best. If multiple advertisers
  // tie on top score, rotate through them stably per query so the
  // same query returns the same pick (no flicker) but different
  // queries share the load.
  scored.sort((a, b) => b.score - a.score);
  const top = scored[0].score;
  const topCandidates = scored.filter((x) => x.score === top);
  const pick = topCandidates[hash % topCandidates.length];
  return pick.ad;
};

// Stable 0-99 hash of a string. Small utility; keeps this file
// dependency-free.
const hashQuery = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h % 100;
};

// 2026-09-08: When the user picks a new state via the header switcher,
// invalidate the adverts pool cache so the very next getRandomAdvert()
// re-fetches with the new state gate applied.
if (typeof window !== 'undefined') {
  window.addEventListener('bp:state-changed', () => {
    advertsCache = [];
    cacheAt = 0;
    cachedState = null;
    shownByScreen.clear();
  });
}

// 2026-09-08: Client-side deactivation list. Set on the frontend so
// house ads (BuildPilot self-promotion) and now-paused campaigns can
// be pulled without needing Supabase write access. To reactivate an
// advertiser, remove their id from this Set.
const DEACTIVATED_ADVERT_IDS = new Set([
  1,   // BuildPilot (house ad) — paused 2026-09-08 per publisher instruction
  2,   // Area Office — paused 2026-09-08 per publisher instruction
]);

const getUserState = async () => {
  // 1) Authoritative: logged-in user's profiles.state.
  try {
    const sb = getSupabase();
    const { data: { user } } = await sb.auth.getUser();
    if (user) {
      const { data, error } = await sb
        .from('profiles')
        .select('state')
        .eq('id', user.id)
        .maybeSingle();
      if (!error && data?.state) return data.state;
    }
  } catch (_e) { /* fall through */ }
  // 2) Anonymous fallback: header state switcher in localStorage.
  //    Fixes: anonymous VIC visitor now sees only VIC-targeted ads
  //    instead of the whole any-state pool.
  try {
    if (typeof window !== 'undefined') {
      const raw = window.localStorage.getItem('bp:userState');
      if (raw) return raw;
    }
  } catch (_e) { /* localStorage disabled */ }
  return null;
};

export const getAdverts = async () => {
  const rawState = await getUserState();
  const userState = normalizeState(rawState);

  if (advertsCache.length > 0 && Date.now() - cacheAt < CACHE_MS && cachedState === userState) {
    return advertsCache;
  }

  try {
    const sb = getSupabase();
    let adverts = [];

    // Advertiser-side rule (Feb 2026): every ad MUST have at least one
    // advert_locations row -- ads with no state targeting never serve.
    // The viewer's state is then used to narrow which of those ads
    // they see; if the viewer has no state (anonymous or profile
    // not set) we serve ads from every advertiser regardless of
    // their state targeting (free users still see ads).
    if (!userState) {
      // No viewer state -> serve ads from any advertiser that has at
      // least one state targeting row.
      const { data: anyLocations } = await sb
        .from('advert_locations')
        .select('advert_id');
      const targetedIds = [...new Set((anyLocations || []).map((r) => r.advert_id))];
      if (targetedIds.length === 0) {
        advertsCache = [];
        cacheAt = Date.now();
        cachedState = userState;
        return advertsCache;
      }
      const { data: allAds, error } = await sb
        .from('adverts')
        .select('*')
        .eq('is_active', true)
        .in('id', targetedIds)
        .order('random_id');
      if (error) return advertsCache;
      advertsCache = (allAds || []).filter((a) => !DEACTIVATED_ADVERT_IDS.has(a.id));
      cacheAt = Date.now();
      cachedState = userState;
      return advertsCache;
    }

    // Viewer has state -> intersect with that state.
    const { data: allLocations } = await sb
      .from('business_locations')
      .select('id, state');
    const matchingLocationIds = (allLocations || [])
      .filter((l) => statesMatch(l.state, userState))
      .map((l) => l.id);

    if (matchingLocationIds.length === 0) {
      advertsCache = [];
      cacheAt = Date.now();
      cachedState = userState;
      return advertsCache;
    }

    const { data: targeted } = await sb
      .from('advert_locations')
      .select('advert_id')
      .in('location_id', matchingLocationIds);
    const targetedIds = [...new Set((targeted || []).map((r) => r.advert_id))];

    if (targetedIds.length === 0) {
      advertsCache = [];
      cacheAt = Date.now();
      cachedState = userState;
      return advertsCache;
    }

    const { data: allAds, error } = await sb
      .from('adverts')
      .select('*')
      .eq('is_active', true)
      .in('id', targetedIds)
      .order('random_id');
    if (error) return advertsCache;
    adverts = (allAds || []).filter((a) => !DEACTIVATED_ADVERT_IDS.has(a.id));

    advertsCache = adverts;
    cacheAt = Date.now();
    cachedState = userState;
    return advertsCache;
  } catch (_e) {
    return advertsCache;
  }
};

export const getRandomAdvert = async (screenLocation) => {
  const ads = await getAdverts();
  if (ads.length === 0) return null;

  // Apply session-wide cap: drop any advert already shown to this
  // visitor >= SESSION_IMPRESSION_CAP times in the current session.
  // If that would leave the pool empty, we serve from the full pool
  // rather than serving nothing (better one over-cap than zero fills).
  const capped = cappedAdvertIds();
  const uncappedAds = capped.size ? ads.filter((a) => !capped.has(a.id)) : ads;
  const pool = uncappedAds.length > 0 ? uncappedAds : ads;

  if (!screenLocation) {
    return pool[Math.floor(Math.random() * pool.length)];
  }

  if (!shownByScreen.has(screenLocation)) shownByScreen.set(screenLocation, new Set());
  const shown = shownByScreen.get(screenLocation);
  const available = pool.filter((a) => !shown.has(a.id));
  if (available.length === 0) {
    shown.clear();
    const pick = pool[Math.floor(Math.random() * pool.length)];
    shown.add(pick.id);
    return pick;
  }
  const pick = available[Math.floor(Math.random() * available.length)];
  shown.add(pick.id);
  return pick;
};

export const trackAdImpression = async (advertId) => {
  // Session-cap accounting — increment the local session counter
  // BEFORE firing the Supabase RPC so the next getRandomAdvert() call
  // sees the updated total. Fire-and-forget on both.
  try {
    const counts = readSessionImpressions();
    counts[advertId] = (counts[advertId] || 0) + 1;
    writeSessionImpressions(counts);
  } catch (_) { /* ignore */ }
  try {
    const sb = getSupabase();
    await sb.rpc('increment_ad_impressions', { ad_id: advertId });
  } catch (_e) { /* fire-and-forget */ }
};

export const trackAdClick = async (advertId, screenLocation) => {
  try {
    const sb = getSupabase();
    await sb.rpc('increment_ad_clicks', { ad_id: advertId });
    const { data: { user } } = await sb.auth.getUser();
    await sb.from('ad_clicks').insert({
      advert_id: advertId,
      user_id: user?.id || null,
      screen_location: screenLocation || 'unknown',
    });
  } catch (_e) { /* fire-and-forget */ }
};
